import React, { useEffect, useRef } from 'react';
import { Header, Icon, Message, Form, Grid, Segment } from 'semantic-ui-react';
import { ResponsibleConsultantSelect } from './ResponsibleConsultantSelect';
import {
  formatContractTokenDisplayLabel,
  formatClusterTokenLabel,
  getContractClusterSaleTokenSelectOptions,
} from '../../utils/contractClusterTokens';
import {
  formatTpclTokenLabel,
  isDhMarketEstado,
  NON_FASTCHIP_DH_TOKEN_VALUE,
} from '../../utils/fastchipClusterRules';
import { SuspensionBlockBanner } from '../clientCancellationLines/SuspensionBlockBanner';

export interface ContractFormContratoDadosTabProps {
  editingContractId: string | null;
  tipoContratacao: string;
  isPrimeiraVenda: boolean;
  setIsPrimeiraVenda: React.Dispatch<React.SetStateAction<boolean>>;
  comprometimentoMinimo: string;
  /** Pré-visualização do cluster que a API usaria (GET suggested-sale-token). */
  suggestedClusterDisplay: string;
  suggestedClusterLoading?: boolean;
  /** Em criação: deve ser token canónico após normalização. Em edição: '' pode significar automático/API. */
  token: string;
  onTokenChange: (value: string) => void;
  diaVencimento: number;
  setDiaVencimento: React.Dispatch<React.SetStateAction<number>>;
  numeroP2B: string;
  setNumeroP2B: React.Dispatch<React.SetStateAction<string>>;
  fastchip: boolean;
  setFastchip: React.Dispatch<React.SetStateAction<boolean>>;
  fastchipDisabled: boolean;
  fastchipDisabledReason: string;
  hasAparelhoInDraft?: boolean;
  /**
   * Pergunta obrigatória: o contrato terá suspensão de linhas?
   * null = ainda não respondeu (bloqueia avanço de aba).
   * true = em geral exige Fastchip (exceto mercado DH por entrega).
   * false = pode escolher fastchip livremente.
   */
  temSuspensao: boolean | null;
  setTemSuspensao: React.Dispatch<React.SetStateAction<boolean | null>>;
  /**
   * TEMPORÁRIO (pedido Eder — bater meta): cluster DH por entrega pode ter suspensão
   * sem converter/exigir Fastchip. ER2 operacional legado não mantém esta excepção.
   */
  allowsSuspensionWithoutFastchip?: boolean;
  /** Regra fim da suspensão (GettelApi PR #553): bloqueia fluxo de suspensão no front. */
  suspensaoBloqueadaContrato?: boolean;
  /** UF do cliente — usada para não converter RJ/MG/ES por entrega em Fastchip/ER2 ao marcar suspensão. */
  clientEstado?: string | null;
  /**
   * True quando Fastchip nao pode ser ativado por falta de ICCIDs/cluster
   * para o estado do cliente. Suspensão exige Fastchip — sem estoque, suspensão fica bloqueada
   * (exceto quando allowsSuspensionWithoutFastchip).
   */
  fastchipUnavailableForRegion: boolean;
  fastchipUnavailableReason: string;
  needsIeMonitoring: boolean;
  tipoFatura: string;
  envioFatura: string;
  origemVenda: string;
  /** Aviso quando Fastchip + PR + DDD Aditivo não combina com o token Senior escolhido. */
  prClusterDddMismatchWarning?: string | null;
  /**
   * Cadastro do cliente + endereço de entrega do formulário (para orientar cluster / supervisor regional).
   * Quando ausente, não mostra o painel de localização.
   */
  tokenLocationContext?: {
    clienteRazaoSocial?: string;
    clienteCidade?: string;
    clienteEstado?: string;
    entregaCidade?: string;
    entregaEstado?: string;
    suggestedCluster?: string;
    suggestedClusterSource?: string;
    suggestedClusterLoading?: boolean;
    prClusterHint?: string;
    hbCpClusterHint?: string;
  } | null;
  showResponsibleConsultantField?: boolean;
  responsibleConsultantUserId?: string;
  onResponsibleConsultantChange?: (userId: string) => void;
  onResponsibleConsultantVisible?: () => void;
  onResponsibleConsultantFocus?: () => void;
  consultantDropdownOptions?: { key: string; value: string; text: string }[];
  loadingConsultantOptions?: boolean;
}

const DIA_VENCIMENTO_OPCOES = [7, 10, 12, 15, 20, 25, 26, 30];

export const ContractFormContratoDadosTab: React.FC<ContractFormContratoDadosTabProps> = ({
  editingContractId,
  tipoContratacao,
  isPrimeiraVenda,
  setIsPrimeiraVenda,
  comprometimentoMinimo,
  suggestedClusterDisplay,
  suggestedClusterLoading,
  token,
  onTokenChange,
  diaVencimento,
  setDiaVencimento,
  numeroP2B,
  setNumeroP2B,
  fastchip,
  setFastchip,
  fastchipDisabled,
  fastchipDisabledReason,
  hasAparelhoInDraft = false,
  temSuspensao,
  setTemSuspensao,
  allowsSuspensionWithoutFastchip = false,
  suspensaoBloqueadaContrato = false,
  clientEstado = null,
  fastchipUnavailableForRegion,
  fastchipUnavailableReason,
  needsIeMonitoring,
  tipoFatura,
  envioFatura,
  origemVenda,
  prClusterDddMismatchWarning,
  tokenLocationContext,
  showResponsibleConsultantField = false,
  responsibleConsultantUserId = '',
  onResponsibleConsultantChange,
  onResponsibleConsultantVisible,
  onResponsibleConsultantFocus,
  consultantDropdownOptions = [],
  loadingConsultantOptions = false,
}) => {
  const consultantVisibilityNotifiedRef = useRef(false);

  useEffect(() => {
    if (!showResponsibleConsultantField) {
      consultantVisibilityNotifiedRef.current = false;
      return;
    }
    if (consultantVisibilityNotifiedRef.current) return;
    consultantVisibilityNotifiedRef.current = true;
    onResponsibleConsultantVisible?.();
  }, [onResponsibleConsultantVisible, showResponsibleConsultantField]);

  const cadastroCidade = (tokenLocationContext?.clienteCidade ?? '').trim();
  const cadastroUf = (tokenLocationContext?.clienteEstado ?? '').trim();
  const entregaCidade = (tokenLocationContext?.entregaCidade ?? '').trim();
  const entregaUf = (tokenLocationContext?.entregaEstado ?? '').trim();
  const temEntregaPreenchida = Boolean(entregaCidade || entregaUf);
  const entregaDifereCadastro =
    temEntregaPreenchida &&
    (entregaUf.toUpperCase() !== cadastroUf.toUpperCase() ||
      entregaCidade.localeCompare(cadastroCidade, undefined, { sensitivity: 'accent' }) !== 0);

  const renderLocationClusterPanel = () => {
    if (!tokenLocationContext) return null;
    const hasLocation =
      cadastroUf || cadastroCidade || (tokenLocationContext.clienteRazaoSocial ?? '').trim();
    if (!hasLocation) return null;

    return (
      <Message info style={{ marginBottom: '14px', maxWidth: '720px' }}>
        <Message.Header>
          <Icon name="map marker alternate" /> Localização e cluster do cliente
        </Message.Header>
        <div style={{ marginTop: '10px', fontSize: '13px', lineHeight: 1.5 }}>
          <p style={{ margin: '0 0 6px' }}>
            <strong>Cadastro do cliente:</strong>{' '}
            {[cadastroCidade, cadastroUf].filter(Boolean).join(' — ') || '—'}
            {tokenLocationContext.clienteRazaoSocial?.trim() ? (
              <span style={{ color: '#555' }}> ({tokenLocationContext.clienteRazaoSocial.trim()})</span>
            ) : null}
          </p>
          {temEntregaPreenchida ? (
            <p style={{ margin: '0 0 6px' }}>
              <strong>Entrega neste contrato:</strong> {entregaCidade || '—'} — {entregaUf || '—'}
              {entregaDifereCadastro ? (
                <span style={{ color: '#1678c2', fontWeight: 600 }}> (diferente do cadastro)</span>
              ) : null}
            </p>
          ) : (
            <p style={{ margin: '0 0 6px', color: '#666' }}>
              <strong>Entrega:</strong> preencha na aba Endereço para comparar com o cadastro.
            </p>
          )}
          <p style={{ margin: '8px 0 0' }}>
            <strong>
              Cluster sugerido (
              {tokenLocationContext.suggestedClusterSource || 'API Senior'}
              ):
            </strong>{' '}
            {tokenLocationContext.suggestedClusterLoading ? (
              <span style={{ color: '#888' }}>A calcular…</span>
            ) : tokenLocationContext.suggestedCluster ? (
              <span style={{ fontWeight: 700, color: '#1678c2' }}>
                {tokenLocationContext.suggestedCluster}
              </span>
            ) : (
              <span style={{ color: '#888' }}>Indisponível — confira UF/cidade do cliente.</span>
            )}
          </p>
          {(tokenLocationContext.prClusterHint || tokenLocationContext.hbCpClusterHint) && (
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#555' }}>
              {tokenLocationContext.prClusterHint ? (
                <>
                  <strong>PR (Fastchip):</strong> cluster {tokenLocationContext.prClusterHint}
                  {tokenLocationContext.prClusterHint === 'MH' ? ' (Guarapuava/MH)' : ' (E.R.GESTAO)'}
                  {' · '}
                </>
              ) : null}
              {tokenLocationContext.hbCpClusterHint ? (
                <>
                  <strong>HB/CP:</strong> mercado {tokenLocationContext.hbCpClusterHint}
                </>
              ) : null}
            </p>
          )}
          {!fastchip && (
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#555' }}>
              Contrato <strong>sem Fastchip</strong>: alinhe o token operacional com a região de entrega e o DDD das
              linhas Aditivo.
            </p>
          )}
        </div>
      </Message>
    );
  };

  const suspensionSelectedYes = temSuspensao === true;
  const suspensionSelectedNo = temSuspensao === false;
  const suspensionPending = temSuspensao === null;
  // Mercado DH (RJ/MG/ES) por entrega: suspensão sem Fastchip (token DH), mesmo sem stock Fastchip.
  const dhDeliveryMarket = !fastchip && isDhMarketEstado(clientEstado);
  const suspensionAllowedWithoutFastchip =
    allowsSuspensionWithoutFastchip || dhDeliveryMarket;
  // Sem estoque Fastchip bloqueia "Sim", salvo exceção temporária DH por entrega.
  const suspensionBlockedByStock =
    fastchipUnavailableForRegion && !suspensionAllowedWithoutFastchip;
  const suspensionFlowBlocked =
    suspensaoBloqueadaContrato || suspensionBlockedByStock;

  return (
    <div style={{ padding: '20px' }}>
      <Header as="h3" style={{ color: '#2c3e50', marginBottom: '20px' }}>
        <Icon name="file alternate" />
        Dados do Contrato
      </Header>

      <p style={{ color: '#666', marginBottom: '20px' }}>
        Preencha as informações principais do contrato que será criado para o cliente selecionado.
      </p>

      <Form>
        <Segment
          style={{
            border: '2px solid #2185d0',
            backgroundColor: '#f8f9ff',
            marginBottom: '20px',
            padding: '20px',
          }}
        >
          <Header as="h4" style={{ color: '#2185d0', marginBottom: '15px' }}>
            <Icon name="star" />
            Tipo de Contrato
          </Header>

          <Form.Field>
            {editingContractId ? (
              <p style={{ margin: 0 }}>
                <strong>Tipo de contratação:</strong>{' '}
                {tipoContratacao || (isPrimeiraVenda ? '1º Venda' : 'Contrato Normal')}
              </p>
            ) : (
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="radio"
                    id="contratoNormal"
                    name="tipoContrato"
                    checked={!isPrimeiraVenda}
                    onChange={() => setIsPrimeiraVenda(false)}
                    style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                  />
                  <label
                    htmlFor="contratoNormal"
                    style={{ cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', color: '#2c3e50' }}
                  >
                    Contrato Normal
                  </label>
                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="radio"
                    id="primeiraVenda"
                    name="tipoContrato"
                    checked={isPrimeiraVenda}
                    onChange={() => setIsPrimeiraVenda(true)}
                    style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                  />
                  <label
                    htmlFor="primeiraVenda"
                    style={{ cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', color: '#2185d0' }}
                  >
                    1º Venda
                  </label>
                </div>
              </div>
            )}

            {isPrimeiraVenda && (
              <Message info style={{ marginTop: '10px' }}>
                <Icon name="info circle" />
                <strong>1º Venda:</strong> Para clientes novos na TIM. Todas as linhas serão do tipo Aditivo.
              </Message>
            )}
            {editingContractId && comprometimentoMinimo !== '' && comprometimentoMinimo !== '0' && (
              <p style={{ margin: '10px 0 0', color: '#666' }}>
                <strong>Comprometimento mínimo:</strong> R${' '}
                {Number(comprometimentoMinimo || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            )}
          </Form.Field>
        </Segment>

        {showResponsibleConsultantField && (
          <Segment style={{ marginBottom: '20px', padding: '20px' }}>
            <Header as="h4" style={{ color: '#2c3e50', marginBottom: '15px' }}>
              <Icon name="user" />
              Consultor responsável
            </Header>
            <Form.Field required>
              <label>Consultor responsável pelo contrato</label>
              <ResponsibleConsultantSelect
                value={responsibleConsultantUserId}
                onChange={(userId) => onResponsibleConsultantChange?.(userId)}
                options={consultantDropdownOptions}
                loading={loadingConsultantOptions}
                onOpen={() => {
                  window.setTimeout(() => onResponsibleConsultantFocus?.(), 0);
                }}
              />
              {loadingConsultantOptions && (
                <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#666' }}>
                  Atualizando lista de consultores...
                </p>
              )}
            </Form.Field>
          </Segment>
        )}

        <Grid stackable>
          <Grid.Row>
            <Grid.Column width={8}>
              <Form.Field required>
                <label>Dia de Vencimento</label>
                <select
                  value={diaVencimento}
                  onChange={(e) => setDiaVencimento(parseInt(e.target.value, 10))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                >
                  <option value="">Selecione...</option>
                  {DIA_VENCIMENTO_OPCOES.map((d) => (
                    <option key={d} value={d}>
                      Dia {d}
                    </option>
                  ))}
                </select>
              </Form.Field>
            </Grid.Column>

            <Grid.Column width={8}>
              <Form.Field>
                <label>Número P2B</label>
                <input
                  type="text"
                  placeholder="Digite o número P2B"
                  value={numeroP2B}
                  onChange={(e) => setNumeroP2B(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>

          {!editingContractId && (
            <Grid.Row>
              <Grid.Column width={16}>
                {suspensaoBloqueadaContrato && (
                  <div style={{ marginBottom: 12 }}>
                    <SuspensionBlockBanner />
                  </div>
                )}
                <Segment
                  data-testid="card-tem-suspensao"
                  style={{
                    border: `2px solid ${
                      suspensionSelectedYes
                        ? '#21ba45'
                        : suspensionSelectedNo
                        ? '#9e9e9e'
                        : '#f2711c'
                    }`,
                    backgroundColor: suspensionSelectedYes
                      ? '#f0fff4'
                      : suspensionSelectedNo
                      ? '#fafafa'
                      : suspensionBlockedByStock
                      ? '#fff8e1'
                      : '#fff5eb',
                    padding: '20px',
                    transition: 'all .15s ease-in-out',
                  }}
                >
                  <Header
                    as="h4"
                    style={{
                      color: suspensionSelectedYes
                        ? '#21ba45'
                        : suspensionSelectedNo
                        ? '#5e5e5e'
                        : '#f2711c',
                      marginBottom: '6px',
                    }}
                  >
                    <Icon
                      name={
                        suspensionSelectedYes
                          ? 'check circle'
                          : suspensionSelectedNo
                          ? 'minus circle'
                          : suspensionBlockedByStock
                          ? 'warning circle'
                          : 'help circle'
                      }
                    />
                    Este contrato terá suspensão de linhas?
                  </Header>
                  <p style={{ marginBottom: '12px', color: '#444', fontSize: '13px' }}>
                    {suspensionAllowedWithoutFastchip ? (
                      <>
                        Ao responder “Sim”, o contrato habilita o fluxo de suspensão neste cluster{' '}
                        <strong>por entrega</strong> (DH), <strong>sem converter para Fastchip</strong>
                        {dhDeliveryMarket ? (
                          <>
                            {' '}
                            e preenche o token <strong>{NON_FASTCHIP_DH_TOKEN_VALUE}</strong>.
                          </>
                        ) : (
                          '.'
                        )}
                      </>
                    ) : (
                      <>
                        Ao responder “Sim”, o contrato habilita o fluxo de suspensão e exige <strong>Fastchip</strong>{' '}
                        (marcado automaticamente quando houver estoque ICCID na região).
                      </>
                    )}
                  </p>
                  {suspensionBlockedByStock && (
                    <Message warning size="tiny" style={{ marginTop: 0, marginBottom: '12px' }}>
                      <Icon name="warning sign" />
                      <strong>Suspensão indisponível:</strong> não há estoque Fastchip para{' '}
                      {fastchipUnavailableReason || 'a região do cliente'}. Aguarde reposição ou crie o contrato sem
                      suspensão.
                    </Message>
                  )}
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      data-testid="btn-tem-suspensao-sim"
                      onClick={() => {
                        if (suspensaoBloqueadaContrato) return;
                        if (suspensionBlockedByStock) {
                          alert(
                            'Suspensão de linhas exige contrato Fastchip. Não há estoque ICCID disponível para a região do cliente no momento.'
                          );
                          return;
                        }
                        // Mercado DH (RJ/MG/ES) por entrega: token DH, NÃO converter para Fastchip/ER2.
                        if (!fastchip && suspensionAllowedWithoutFastchip) {
                          if (isDhMarketEstado(clientEstado) || !token?.trim()) {
                            onTokenChange(NON_FASTCHIP_DH_TOKEN_VALUE);
                          }
                          setTemSuspensao(true);
                          return;
                        }
                        setTemSuspensao(true);
                        // TEMPORÁRIO (pedido Eder — bater meta): DH por entrega não converte para Fastchip.
                        if (!fastchip && !suspensionAllowedWithoutFastchip) setFastchip(true);
                      }}
                      disabled={suspensionFlowBlocked}
                      style={{
                        flex: '1 1 140px',
                        padding: '10px 14px',
                        border: `2px solid ${suspensionSelectedYes ? '#21ba45' : '#bbb'}`,
                        backgroundColor: suspensionSelectedYes ? '#21ba45' : '#fff',
                        color: suspensionSelectedYes ? '#fff' : '#333',
                        fontWeight: 'bold',
                        borderRadius: '6px',
                        cursor: suspensionFlowBlocked ? 'not-allowed' : 'pointer',
                        opacity: suspensionFlowBlocked && !suspensionSelectedYes ? 0.55 : 1,
                      }}
                    >
                      <Icon name="check" /> SIM, terá suspensão
                    </button>
                    <button
                      type="button"
                      data-testid="btn-tem-suspensao-nao"
                      onClick={() => {
                        setTemSuspensao(false);
                        if (fastchipUnavailableForRegion && fastchip) {
                          setFastchip(false);
                        }
                      }}
                      style={{
                        flex: '1 1 140px',
                        padding: '10px 14px',
                        border: `2px solid ${suspensionSelectedNo ? '#9e9e9e' : '#bbb'}`,
                        backgroundColor: suspensionSelectedNo ? '#9e9e9e' : '#fff',
                        color: suspensionSelectedNo ? '#fff' : '#333',
                        fontWeight: 'bold',
                        borderRadius: '6px',
                        cursor: 'pointer',
                      }}
                    >
                      <Icon name="close" /> NÃO terá suspensão
                    </button>
                  </div>
                  {suspensionPending && (
                    <Message warning size="tiny" style={{ marginTop: '12px', marginBottom: 0 }}>
                      <Icon name="exclamation triangle" /> Responda para liberar a próxima aba.
                    </Message>
                  )}
                </Segment>
              </Grid.Column>
            </Grid.Row>
          )}

          <Grid.Row>
            <Grid.Column width={16}>
              <Form.Field>
                {editingContractId ? (
                  <p style={{ margin: 0 }}>
                    <strong>Fastchip:</strong> {fastchip ? 'Sim' : 'Não'}
                  </p>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <input
                      type="checkbox"
                      id="fastchip"
                      checked={fastchip}
                      onChange={(e) => !fastchipDisabled && setFastchip(e.target.checked)}
                      disabled={fastchipDisabled}
                      data-testid="checkbox-fastchip"
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: fastchipDisabled ? 'not-allowed' : 'pointer',
                        opacity: fastchipDisabled ? 0.6 : 1,
                      }}
                    />
                    <label
                      htmlFor="fastchip"
                      style={{
                        margin: 0,
                        cursor: fastchipDisabled ? 'not-allowed' : 'pointer',
                        flex: 1,
                      }}
                    >
                      <strong>Fastchip</strong> - Marque se o cliente deseja receber um chip
                      {temSuspensao === true && !suspensionAllowedWithoutFastchip && (
                        <Icon
                          name="lock"
                          style={{ marginLeft: '6px', color: '#f2711c' }}
                          title="Travado porque o contrato terá suspensão de linhas"
                        />
                      )}
                    </label>
                    {fastchipDisabled && fastchipDisabledReason && (
                      <span
                        style={{
                          color: fastchip ? '#5cb85c' : '#d9534f',
                          fontSize: '12px',
                          fontStyle: 'italic',
                          marginLeft: '10px',
                          fontWeight: 'bold',
                        }}
                      >
                        ({fastchip ? 'Obrigatório' : 'Indisponível'}: {fastchipDisabledReason})
                      </span>
                    )}
                  </div>
                )}
              </Form.Field>
              {hasAparelhoInDraft && (
                <Message warning size="small" style={{ marginTop: editingContractId ? '10px' : '6px' }}>
                  <Icon name="mobile alternate" />
                  Este contrato possui linha com aparelho (incluindo WTTX). Nesse caso o envio é por entrega da operadora
                  e o Fastchip deve permanecer desmarcado.
                </Message>
              )}
              {needsIeMonitoring && (
                <Form.Field>
                  <div
                    style={{
                      backgroundColor: fastchip ? '#e8f5e9' : '#fff3cd',
                      border: `1px solid ${fastchip ? '#81c784' : '#ffc107'}`,
                      borderRadius: '4px',
                      padding: '12px',
                      marginTop: '10px',
                      display: 'flex',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Icon
                      name={fastchip ? 'check circle' : 'warning'}
                      style={{
                        color: fastchip ? '#2e7d32' : '#856404',
                        marginRight: '10px',
                        marginTop: '2px',
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <strong
                        style={{
                          color: fastchip ? '#2e7d32' : '#856404',
                          display: 'block',
                          marginBottom: '4px',
                        }}
                      >
                        {fastchip
                          ? 'Cliente IE inapta — contrato Fastchip permitido'
                          : 'Atenção: cliente com IE inapta'}
                      </strong>
                      <p style={{ color: fastchip ? '#2e7d32' : '#856404', margin: 0, fontSize: '13px' }}>
                        {fastchip
                          ? 'Este cliente não possui inscrição estadual ativa no UF, mas contratos Fastchip podem ser criados normalmente, sem bypass de IE.'
                          : 'Este cliente não possui inscrição estadual ativa ou a inscrição pode estar baixada. Contratos por entrega (sem Fastchip) com linhas Aditivo exigem bypass de IE. Para evitar o bloqueio, marque Fastchip neste contrato.'}
                      </p>
                      {tokenLocationContext && (cadastroUf || cadastroCidade) && (
                        <p style={{ color: fastchip ? '#2e7d32' : '#856404', margin: '8px 0 0', fontSize: '12px' }}>
                          <strong>Cliente:</strong> {[cadastroCidade, cadastroUf].filter(Boolean).join(' — ')}
                          {tokenLocationContext.suggestedClusterLoading ? (
                            <> · <em>calculando cluster…</em></>
                          ) : tokenLocationContext.suggestedCluster ? (
                            <> · <strong>Cluster sugerido: {tokenLocationContext.suggestedCluster}</strong></>
                          ) : null}
                        </p>
                      )}
                    </div>
                  </div>
                </Form.Field>
              )}
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={16}>
              {renderLocationClusterPanel()}
              <Form.Field required={!editingContractId}>
                <label>Token de cluster</label>
                <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#666' }}>
                  <strong>Suspensão</strong> e <strong>Fastchip</strong> estão <strong>acima</strong>. Depois escolha o
                  cluster conforme o <strong>DDD das linhas Aditivo</strong> e a região do cliente. Com Fastchip
                  desmarcado a lista mostra tokens <strong>operacionais TIM</strong>; com Fastchip, tokens{' '}
                  <strong>Senior</strong>.
                  {!editingContractId && (
                    <>
                      {' '}
                      <strong>Ao criar contrato novo</strong>, a escolha na lista é <strong>obrigatória</strong>.
                    </>
                  )}
                </p>
                <select
                  value={token}
                  onChange={(e) => onTokenChange(e.target.value)}
                  required={!editingContractId}
                  style={{
                    width: '100%',
                    maxWidth: '520px',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                >
                  {editingContractId ? (
                    <option value="">
                      {fastchip
                        ? 'Deixar a API definir pelo cadastro/entrega (Senior) ou escolha um token na lista'
                        : 'Deixar vazio (API resolve ao gravar) ou escolha o token operacional na lista'}
                    </option>
                  ) : (
                    <option value="" disabled>
                      Selecione um token de cluster…
                    </option>
                  )}
                  {getContractClusterSaleTokenSelectOptions(fastchip, token).map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#888' }}>
                  Referência HB/CP/TPCL: {formatClusterTokenLabel('HB', 'operational')} ·{' '}
                  {formatClusterTokenLabel('CP', 'operational')} · Fastchip:{' '}
                  {formatTpclTokenLabel('fastchip')} · Por entrega: {formatTpclTokenLabel('operational')}
                </p>
                <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#666' }}>
                  <strong>Pré-visualização (API Senior):</strong>{' '}
                  {suggestedClusterLoading ? (
                    <span style={{ color: '#888' }}>A calcular…</span>
                  ) : suggestedClusterDisplay ? (
                    <span>{suggestedClusterDisplay}</span>
                  ) : (
                    <span style={{ color: '#888' }}>
                      Selecione um cliente (e preencha a entrega, se aplicável) para ver a sugestão Senior.
                    </span>
                  )}
                </p>
                <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#666' }}>
                  {editingContractId ? (
                    fastchip ? (
                      <>
                        Com Fastchip, a primeira opção deixa a API aplicar o cluster Senior por UF/cidade (e pode ser
                        ajustado por <strong>DDD 42 = MH</strong> e <strong>41/43/44/45 = E.R.GESTAO</strong> no PR nas
                        linhas Aditivo). Escolha um token explícito na lista para gravar esse valor.
                      </>
                    ) : (
                      <>
                        Sem Fastchip, use um token <strong>operacional TIM</strong> da lista. A sugestão Senior acima
                        é só referência; não preenche automaticamente esses códigos.
                      </>
                    )
                  ) : fastchip ? (
                    <>
                      Com Fastchip, o sistema tenta <strong>preencher automaticamente</strong> o token Senior a partir do
                      DDD (telefone do cliente/administrador ou primeira linha Aditivo). A lista mostra os DDDs de cada
                      cluster; pode alterar manualmente se necessário.
                    </>
                  ) : (
                    <>
                      Sem Fastchip, escolha um token <strong>operacional TIM</strong> da lista. A pré-visualização
                      Senior acima é só referência.
                    </>
                  )}
                </p>
                {prClusterDddMismatchWarning && (
                  <Message warning size="small" style={{ marginTop: '12px', maxWidth: '640px' }}>
                    <Icon name="warning sign" />
                    {prClusterDddMismatchWarning}
                  </Message>
                )}
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>

      <Segment style={{ marginTop: '20px', backgroundColor: '#f8f9fa' }}>
        <Header as="h4" style={{ color: '#2c3e50' }}>
          <Icon name="info circle" />
          Resumo do Contrato
        </Header>
        <Grid stackable>
          <Grid.Row>
            <Grid.Column width={8}>
              <p>
                <strong>Vencimento:</strong> {diaVencimento ? `Dia ${diaVencimento}` : 'Não informado'}
              </p>
              <p>
                <strong>Valor Mensal:</strong> Calculado automaticamente
              </p>
              <p>
                <strong>Tipo Fatura:</strong> {tipoFatura || 'Não informado'}
              </p>
            </Grid.Column>
            <Grid.Column width={8}>
              <p>
                <strong>Data do Contrato:</strong> {new Date().toLocaleDateString('pt-BR')}
              </p>
              <p>
                <strong>Data Venda:</strong> {new Date().toLocaleDateString('pt-BR')}
              </p>
              <p>
                <strong>Envio Fatura:</strong> {envioFatura || 'Não informado'}
              </p>
              <p>
                <strong>Origem Venda:</strong> {origemVenda || 'Não informado'}
              </p>
              <p>
                <strong>Token cluster:</strong>{' '}
                {token
                  ? formatContractTokenDisplayLabel(token, fastchip)
                  : suggestedClusterDisplay
                    ? fastchip
                      ? `Sugestão Senior (API): ${suggestedClusterDisplay}`
                      : `Referência Senior (API): ${suggestedClusterDisplay}`
                    : editingContractId
                      ? 'Não definido (API resolve ao gravar se vazio)'
                      : 'Pendente — selecione um token na lista acima'}
              </p>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>
    </div>
  );
};
